import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Identifies which operation a notification belongs to, for Apollo's dedup guard.
 */
export type QueryNotificationIdentity = {
	/**
	 * Compared by reference: Relay `graphql` artifacts are module-level constants, so a
	 * different operation is always a different object. Deep-comparing a `ConcreteRequest`
	 * would be needlessly expensive.
	 */
	query: unknown;
	/** Deep-compared, mirroring Apollo's `equal(previousOptions.variables, variables)`. */
	variables: unknown;
};

/**
 * A queued `onCompleted`/`onError` invocation awaiting the next commit.
 */
export type PendingQueryNotification<TData> = (
	| { type: 'completed'; data: TData }
	| { type: 'error'; error: any }
) & { identity: QueryNotificationIdentity };

type UseDeferredQueryNotificationsOptions<TData> = {
	/** Stable `onCompleted` (e.g. from `useStableCallback`). */
	onCompleted: (data: TData) => void;
	/** Stable `onError` (e.g. from `useStableCallback`). */
	onError: (error: any) => void;
	/**
	 * Whether the caller supplied either callback. When it did not, nothing is queued
	 * at all — otherwise every query result would pay for a state update whose only
	 * effect is to invoke two no-ops.
	 */
	hasCallbacks: boolean;
};

/**
 * Restores Apollo's post-commit `onCompleted`/`onError` contract for the Relay
 * interop hooks.
 *
 * Apollo invokes these callbacks from an effect (`QueryData.afterExecute` ->
 * `handleErrorOrCompleted`, keyed on the result data), so by the time a caller's
 * callback runs, the render-scope `data`/`loading`/`error` it closes over already
 * describe the result being reported. Relay's observables and promises call back
 * synchronously, while the `setState` calls for that same result are still queued —
 * invoking the callbacks inline exposes pre-update values and breaks that contract.
 *
 * Queue at the point the result arrives; flush once the state has committed.
 *
 * It also reproduces Apollo's dedup guard — see `markLoading` on the return value.
 *
 * Both returned functions are referentially stable for the lifetime of the hook, so
 * they are safe to list in the dependency array of effects and callbacks that issue
 * requests — a changing identity there would re-trigger the request.
 *
 * Notifications queued after unmount never flush, which also matches Apollo (its
 * effect cannot run once unmounted).
 */
export function useDeferredQueryNotifications<TData>({
	onCompleted,
	onError,
	hasCallbacks,
}: UseDeferredQueryNotificationsOptions<TData>): {
	queueNotification: (notification: PendingQueryNotification<TData>) => void;
	/**
	 * Must be called wherever the hook enters a loading state for a new request.
	 *
	 * This is the input to Apollo's dedup guard: Apollo suppresses `onCompleted` when it
	 * has reported before, the previous observed result was not loading, and neither
	 * query nor variables have changed. Recording the loading transition is what lets a
	 * refetch or a `notifyOnNetworkStatusChange` poll tick report again, while a bare
	 * repeat result for the same operation stays silent.
	 */
	markLoading: () => void;
} {
	const pendingNotificationsRef = useRef<PendingQueryNotification<TData>[]>([]);
	const [notificationTick, setNotificationTick] = useState(0);
	// Held in a ref, and deliberately not a `useCallback` dependency: a caller may
	// supply callbacks conditionally, and letting that flip `queueNotification`'s
	// identity would re-run the request-issuing effects that depend on it.
	const hasCallbacksRef = useRef(false);
	hasCallbacksRef.current = hasCallbacks;

	// Apollo's `previousOptions` / `previousData.loading`, respectively.
	const previousIdentityRef = useRef<QueryNotificationIdentity | null>(null);
	const followedLoadingRef = useRef(false);

	const markLoading = useCallback(() => {
		followedLoadingRef.current = true;
	}, []);

	const queueNotification = useCallback((notification: PendingQueryNotification<TData>) => {
		if (!hasCallbacksRef.current) {
			return;
		}

		const followedLoading = followedLoadingRef.current;
		followedLoadingRef.current = false;
		const previousIdentity = previousIdentityRef.current;
		// Apollo advances `previousOptions` on every observation, including suppressed
		// ones, so this is updated before the guard returns.
		previousIdentityRef.current = notification.identity;

		// Apollo: react-hooks QueryData.handleErrorOrCompleted
		//   if (previousOptions && !previousData.loading
		//       && equal(previousOptions.query, query)
		//       && equal(previousOptions.variables, variables)) return;
		if (
			previousIdentity &&
			!followedLoading &&
			previousIdentity.query === notification.identity.query &&
			isEqual(previousIdentity.variables, notification.identity.variables)
		) {
			return;
		}

		pendingNotificationsRef.current.push(notification);
		setNotificationTick((tick) => tick + 1);
	}, []);

	useEffect(() => {
		if (pendingNotificationsRef.current.length === 0) {
			return;
		}
		// Swap the queue out before invoking anything: a callback may itself trigger
		// another request that queues onto it.
		const notifications = pendingNotificationsRef.current;
		pendingNotificationsRef.current = [];
		notifications.forEach((notification) => {
			if (notification.type === 'completed') {
				onCompleted(notification.data);
			} else {
				onError(notification.error);
			}
		});
	}, [notificationTick, onCompleted, onError]);

	return { queueNotification, markLoading };
}
